import { useState, useMemo, createContext, useContext, useEffect } from "react";
import axios from "axios";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList
} from "recharts";
import { BASE_URL } from "../../../config/api";
import { useNavigate } from "react-router-dom";

// ── Base API Configurations ────────────────────────────────────────────────
function getParams() {
  return {};
}

// ── Date helpers ───────────────────────────────────────────────────────────
function toDateStr(date) {
  return date.toISOString().slice(0, 10); // "YYYY-MM-DD"
}
function offsetDate(base, days) {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}

const TODAY = new Date();

// ── Date Range Context ─────────────────────────────────────────────────────
const DashboardDataCtx = createContext(null);
function useDashboardData() { return useContext(DashboardDataCtx); }

// ── Date Range Picker ──────────────────────────────────────────────────────
const PRESETS = [
  { label: "7d",   days: 7 },
  { label: "14d", days: 14 },
  { label: "30d", days: 30 },
  { label: "60d", days: 60 },
  { label: "90d", days: 90 },
];

export function DateRangePicker({ from, to, onChange }) {
  const [activePreset, setActivePreset] = useState("");

  function applyPreset(days, label) {
    setActivePreset(label);
    const f = toDateStr(offsetDate(TODAY, -(days - 1)));
    const t = toDateStr(TODAY);
    onChange(f, t);
  }

  function handleFrom(e) {
    setActivePreset("custom");
    onChange(e.target.value, to);
  }
  function handleTo(e) {
    setActivePreset("custom");
    onChange(from, e.target.value);
  }

  const dayCount = useMemo(() => {
    const diff = (new Date(to) - new Date(from)) / 86400000 + 1;
    return Math.max(0, Math.round(diff));
  }, [from, to]);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 px-4 py-3 flex flex-wrap items-center gap-3 mb-4">
      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <i className="ti ti-calendar text-sm" />
        <span>Date range</span>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {PRESETS.map(p => (
          <button
            key={p.label}
            onClick={() => applyPreset(p.days, p.label)}
            className={`text-xs px-3 py-1 rounded-full border transition-all font-medium ${
              activePreset === p.label
                ? "bg-[#6C63FF] text-white border-[#6C63FF]"
                : "bg-white text-slate-500 border-slate-200 hover:border-[#6C63FF] hover:text-[#6C63FF]"
            }`}
          >
            Last {p.label}
          </button>
        ))}
      </div>

      <div className="w-px h-5 bg-slate-100" />

      <div className="flex items-center gap-2">
        <input
          type="date"
          value={from}
          max={to}
          onChange={handleFrom}
          className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 focus:outline-none focus:border-[#6C63FF]"
        />
        <span className="text-slate-300 text-xs">→</span>
        <input
          type="date"
          value={to}
          min={from}
          max={toDateStr(TODAY)}
          onChange={handleTo}
          className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 focus:outline-none focus:border-[#6C63FF]"
        />
      </div>

      <span className="ml-auto text-[11px] font-medium px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600">
        {dayCount} day{dayCount !== 1 ? "s" : ""} selected
      </span>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────
function MetricCards({ items }) {
  return (
    <div className="flex flex-wrap gap-2 mb-3">
      {items.map((m) => (
        <div key={m.label} className="flex-1 min-w-[100px] bg-slate-50 rounded-xl px-3 py-2.5">
          <p className="text-[10px] text-slate-400 mb-0.5 uppercase tracking-wide font-medium">{m.label}</p>
          <p className="text-base font-semibold" style={{ color: m.color }}>{m.value.toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

function LegendRow({ items }) {
  return (
    <div className="flex flex-wrap gap-3 mt-2">
      {items.map((item) => (
        <span key={item.label} className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className="w-2 h-2 rounded-sm inline-block flex-shrink-0" style={{ background: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const seen = new Set();

  const items = payload.filter((p) => {
    if (seen.has(p.dataKey)) return false;
    seen.add(p.dataKey);
    return true;
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-lg text-xs min-w-[120px]">
      <p className="text-slate-400 font-medium mb-1.5 text-[10px] uppercase tracking-wide">
        {label}
      </p>

      {items.map((p, i) => (
        <div key={i} className="flex justify-between gap-4 mb-0.5">
          <span className="flex items-center gap-1.5 text-slate-500">
            <span
              className="w-1.5 h-1.5 rounded-sm inline-block"
              style={{ background: p.color || p.stroke }}
            />
            {p.name}
          </span>

          <span className="font-semibold text-slate-800">
            {p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function ProductTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const items = payload.map((p) => ({
    ...p,
    actualValue:
      Number(p.payload?._raw?.[p.dataKey]) || 0,
  }));

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-lg text-xs min-w-[120px]">
      <p className="text-slate-400 font-medium mb-1.5 text-[10px] uppercase tracking-wide">
        {label}
      </p>

      {items.map((p, i) => (
        <div key={i} className="flex justify-between gap-4 mb-0.5">
          <span className="flex items-center gap-1.5 text-slate-500">
            <span
              className="w-1.5 h-1.5 rounded-sm inline-block"
              style={{ background: p.color || p.stroke }}
            />
            {p.name}
          </span>

          <span className="font-semibold text-slate-800">
            {p.actualValue}
          </span>
        </div>
      ))}
    </div>
  );
}

const axisProps = { axisLine: false, tickLine: false, tick: { fontSize: 9, fill: "#94a3b8" } };
function xInterval(len) { return len > 20 ? Math.floor(len / 8) : 1; }

// Dynamic formatter rule to prevent rendering '0' values directly on cluttered graphs
const labelFormatter = (value) => (value > 0 ? value.toLocaleString() : "");

function Donut({ pct, color = "#6C63FF", size = 88 }) {
  const r = size * 0.4, circ = 2 * Math.PI * r;
  const percentage = isNaN(pct) ? 0 : pct;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f0eeff" strokeWidth={9} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={9}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - percentage / 100)} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold" style={{ color }}>{percentage}%</div>
    </div>
  );
}

function StatCard({
  icon,
  iconBg,
  iconColor,
  value,
  label,
  arrow,
  onClick,
}) {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-2xl flex items-center gap-3 px-4 py-4 border border-slate-100 ${
        onClick ? "cursor-pointer hover:shadow-md transition-all" : ""
      }`}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
        style={{ background: iconBg, color: iconColor }}
      >
        <i className={`ti ${icon}`} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-lg font-semibold text-slate-800 leading-none">
          {typeof value === "number"
            ? value.toLocaleString()
            : value || 0}
        </p>
        <p className="text-[11px] text-slate-400 mt-0.5">{label}</p>
      </div>

      {arrow && <span className="text-slate-300 text-base">›</span>}
    </div>
  );
}

function GoalBar({ label, value, today, color = "#6C63FF" }) {
  const pct = today > 0 ? Math.min(100, Math.round((value / today) * 100)) : 0;
  return (
    <div className="bg-white rounded-2xl px-4 py-3.5 border border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className="text-sm font-semibold" style={{ color }}>{pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <p className="text-[10px] text-slate-400 mt-1.5">{value} Overall · Todays {today}</p>
    </div>
  );
}

// ── 1. Retailer Onboarding ─────────────────────────────────────────────────
export function OnboardingChart() {
  const { onboardingData } = useDashboardData();
  const total = onboardingData.reduce((s, r) => s + r.count, 0);
  const peak = onboardingData.reduce((a, b) => (b.count > a.count ? b : a), onboardingData[0] || {});
  
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
      <h3 className="text-sm font-semibold text-slate-800">Retailer Onboarding</h3>
      <p className="text-[11px] text-slate-400 mt-0.5 mb-4">
        Total: <span className="text-blue-600 font-semibold">{total}</span> · Peak:{" "}
        <span className="text-blue-800 font-semibold">{peak.count || 0}</span>
      </p>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={onboardingData} barSize={onboardingData.length > 20 ? 8 : 14} margin={{ top: 15, right: 4, left: -24, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="day" {...axisProps} interval={xInterval(onboardingData.length)} />
          <YAxis {...axisProps} allowDecimals={false} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
          <Bar dataKey="count" name="Onboarded" radius={[3, 3, 0, 0]}>
            <LabelList dataKey="count" position="top" formatter={labelFormatter} style={{ fill: "#475569", fontSize: 10, fontWeight: 600 }} />
            {onboardingData.map((e, i) => (
              <Cell key={i} fill={e.count === 0 ? "#dbeafe" : e.count === peak.count ? "#1d4ed8" : "#3b82f6"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <LegendRow items={[{ label: "Onboarded", color: "#3b82f6" }, { label: "No activity", color: "#dbeafe" }, { label: "Peak day", color: "#1d4ed8" }]} />
    </div>
  );
}

const RETAILER_SERIES = [
  { key: "using", name: "Using", color: "#4F46E5" }, 
  { key: "notUsing", name: "Not Using", color: "#6366F1" },
  { key: "pending", name: "Pending", color: "#A5B4FC" }, // 👈 Darkened from #C7D2FE
];

export function RetailerAnalysisChart() {
  const { statusData } = useDashboardData();

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 max-w-4xl">
      <h3 className="text-sm font-semibold text-slate-800">Retailer Status Analysis</h3>
      <p className="text-[11px] text-slate-400 mt-0.5 mb-4">Daywise breakdown by retailer status</p>
      
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={statusData}
          barSize={12} /* 👈 Decreased bar width to look sleek like the image */
          barGap={0}
        >
          <defs>
            {/* Gradients to recreate the exact soft-shaded capsule look */}
            <linearGradient id="usingGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4338CA" />
              <stop offset="100%" stopColor="#4F46E5" />
            </linearGradient>

            <linearGradient id="notUsingGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366F1" />
              <stop offset="100%" stopColor="#818CF8" />
            </linearGradient>

            <linearGradient id="pendingGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#A5B4FC" />  {/* 👈 Richer, deeper top shade */}
              <stop offset="100%" stopColor="#C7D2FE" /> {/* 👈 Smooth transition to the previous shade */}
            </linearGradient>
          </defs>

          <CartesianGrid
            vertical={false}
            stroke="#F1F5F9"
            strokeDasharray="3 3"
          />

          <XAxis
            dataKey="day"
            tickLine={false}
            axisLine={false}
            tick={{ fill: "#94A3B8", fontSize: 11 }}
            dy={8}
          />

          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: "#94A3B8", fontSize: 11 }}
            allowDecimals={false}
            dx={-8}
          />

          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />

          {/* Base Layer */}
          <Bar 
            dataKey="using" 
            stackId="a" 
            fill="url(#usingGrad)" 
          />

          {/* Middle Layer */}
          <Bar 
            dataKey="notUsing" 
            stackId="a" 
            fill="url(#notUsingGrad)" 
          />

          {/* Top Layer: This holds the rounded corners so the total capsule is clean */}
          <Bar 
            dataKey="pending" 
            stackId="a" 
            fill="url(#pendingGrad)" 
            radius={[6, 6, 0, 0]} /* 👈 Rounds only the top of the stack */
          />
        </BarChart>
      </ResponsiveContainer>

      {/* Dynamic or Static Legend Row */}
      <LegendRow items={RETAILER_SERIES.map(s => ({ label: s.name, color: s.color }))} />
    </div>
  );
}

// ── 3. Warranty Sales ──────────────────────────────────────────────────────
export function WarrantySalesChart() {
  const { salesData } = useDashboardData();
  const total = salesData.reduce((s, r) => s + r.sales, 0);
  const avg = Math.round(total / (salesData.length || 1));
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
      <h3 className="text-sm font-semibold text-slate-800">Warranty Sales</h3>
      <p className="text-[11px] text-slate-400 mt-0.5 mb-4">
        Total: <span className="text-blue-600 font-semibold">{total.toLocaleString()}</span> · Daily avg:{" "}
        <span className="text-emerald-600 font-semibold">{avg}</span>
      </p>
      <ResponsiveContainer width="100%" height={170}>
        <AreaChart data={salesData} margin={{ top: 15, right: 4, left: -24, bottom: 0 }}>
          <defs>
            <linearGradient id="wsGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="day" {...axisProps} interval={xInterval(salesData.length)} />
          <YAxis {...axisProps} allowDecimals={false} />
          <Tooltip content={<CustomTooltip />} />
          <Area dataKey="sales" name="Warranty sales" stroke="#3b82f6" strokeWidth={2}
            fill="url(#wsGrad)" dot={true} activeDot={{ r: 4, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }}>
            <LabelList dataKey="sales" position="top" formatter={labelFormatter} style={{ fill: "#2563eb", fontSize: 10, fontWeight: 600 }} />
          </Area>
        </AreaChart>
      </ResponsiveContainer>
      <LegendRow items={[{ label: "Warranty sales", color: "#3b82f6" }]} />
    </div>
  );
}

// ── 4. Lead Chart ──────────────────────────────────────────────────────────
export function LeadChart() {
  const { leadData, leadSummary } = useDashboardData();
  const {
    created = 0,
    in_progress = 0,
    won = 0,
    reject = 0,
    conversion_rate = 0,
  } = leadSummary || {};
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
      <h3 className="text-sm font-semibold text-slate-800">Lead Created vs Won</h3>
      <p className="text-[11px] text-slate-400 mt-0.5 mb-3">Daywise lead pipeline and conversion</p>
      <MetricCards
        items={[
          {
            label: "Created",
            value: created,
            color: "#818cf8",
          },
          {
            label: "Won",
            value: won,
            color: "#10b981",
          },
          {
            label: "In Progress",
            value: in_progress,
            color: "#f59e0b",
          },
          {
            label: "Rejected",
            value: reject,
            color: "#ef4444",
          },
          {
            label: "Conversion",
            value: `${conversion_rate}%`,
            color: "#a855f7",
          },
        ]}
      />
      <ResponsiveContainer width="100%" height={180}>
  <BarChart
    data={leadData}
    barSize={10}
    margin={{ top: 15, right: 4, left: -24, bottom: 0 }}
  >
    <CartesianGrid vertical={false} stroke="#f1f5f9" />

    <XAxis
      dataKey="day"
      {...axisProps}
      interval={xInterval(leadData.length)}
    />

    <YAxis {...axisProps} allowDecimals={false} />

    <Tooltip content={<CustomTooltip />} />

   <Bar
  dataKey="created"
  name="Created"
  fill="#818cf8"
  radius={[3, 3, 0, 0]}
>
  <LabelList
    dataKey="created"
    position="top"
    formatter={labelFormatter}
    style={{
      fill: "#4f46e5",
      fontSize: 9,
      fontWeight: 600,
    }}
  />
</Bar>

<Bar
  dataKey="inProgress"
  name="In Progress"
  fill="#f59e0b"
  radius={[3, 3, 0, 0]}
>
  <LabelList
    dataKey="inProgress"
    position="top"
    formatter={labelFormatter}
    style={{
      fill: "#d97706",
      fontSize: 9,
      fontWeight: 600,
    }}
  />
</Bar>

<Bar
  dataKey="won"
  name="Won"
  fill="#10b981"
  radius={[3, 3, 0, 0]}
>
  <LabelList
    dataKey="won"
    position="top"
    formatter={labelFormatter}
    style={{
      fill: "#059669",
      fontSize: 9,
      fontWeight: 600,
    }}
  />
</Bar>

<Bar
  dataKey="reject"
  name="Rejected"
  fill="#ef4444"
  radius={[3, 3, 0, 0]}
>
  <LabelList
    dataKey="reject"
    position="top"
    formatter={labelFormatter}
    style={{
      fill: "#dc2626",
      fontSize: 9,
      fontWeight: 600,
    }}
  />
</Bar>
  </BarChart>
</ResponsiveContainer>
      <LegendRow items={[{ label: "Created", color: "#818cf8" }, { label: "Won", color: "#34d399" }]} />
    </div>
  );
}

// ── 5. Product Warranty Sales ─────────────────────────────────────────────
const PALETTE = ["#818cf8", "#34d399", "#fb923c", "#38bdf8", "#a855f7", "#ec4899", "#f59e0b"];

export function ProductWarrantyChart({reportType, setReportType}) {
  const { productData, productTotals } = useDashboardData();

  const { products, chartData } = useMemo(() => {
    const keys = new Set();
    productData.forEach((d) => {
      Object.keys(d).forEach((k) => {
        if (k !== "date" && k !== "day") {
          keys.add(k);
        }
      });
    });

    const productKeys = Array.from(keys);
    const lastValues = {};

    const formatted = productData.map((item) => {
      const row = {
        date: item.date,
        day: new Date(item.date).getDate(),
        _raw: {}, // actual API values
      };

      productKeys.forEach((key) => {
        const actualValue = Number(item[key] || 0);

        row._raw[key] = actualValue;

        if (actualValue > 0) {
          lastValues[key] = actualValue;
          row[key] = actualValue;
        } else {
          row[key] = lastValues[key] ?? 0;
        }
      });

      return row;
    });

    const dynamicProducts = productKeys.map((key, index) => ({
      key,
      name: key.charAt(0).toUpperCase() + key.slice(1),
      color: PALETTE[index % PALETTE.length],
    }));

    return { products: dynamicProducts, chartData: formatted };
  }, [productData]);

    const totals = useMemo(() => {
      const result = {};

      Object.entries(productTotals || {}).forEach(([key, value]) => {
        result[key] = value.count;
      });

      return result;
    }, [productTotals]);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
      <div className="flex justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Product Warranty Sales</h3>
          <p className="text-[11px] text-slate-400 mt-0.5 mb-3">Daywise product-wise warranty sales trend</p>
          </div>
      <div>
          <select
          value={reportType}
          onChange={(e) => setReportType(e.target.value)}
          className="border rounded-lg px-3 py-2"
        >
          <option value="products">Products</option>
          <option value="product_types">Product Types</option>
        </select>
          </div>
        </div>
      
      {products.length > 0 ? (
        <>
          <MetricCards
            items={products.map((p) => ({
              label: p.name,
              value: totals[p.key] || 0,
              color: p.color,
            }))}
          />
          <ResponsiveContainer width="100%" height={130}>
            <LineChart data={chartData} margin={{ top: 15, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="day" {...axisProps} interval={xInterval(chartData.length)} />
              <YAxis {...axisProps} allowDecimals={false} />
              <Tooltip content={<ProductTooltip />} />
              {products.map((p, i) => (
                <Line
                  key={p.key}
                  dataKey={p.key}
                  name={p.name}
                  stroke={p.color}
                  strokeWidth={2}
                  dot={(props) => {
                    const { payload, cx, cy } = props;

                    const actualValue = Number(payload?._raw?.[p.key]) || 0;

                    if (actualValue <= 0) return null;

                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={3}
                        fill={p.color}
                        stroke={p.color}
                      />
                    );
                  }}
                  strokeDasharray={i > 1 ? "4 3" : undefined}
                  activeDot={{ r: 4, fill: p.color, stroke: "#fff", strokeWidth: 2 }}
                >
                  {/* ─── Fixed Label Rendering using content prop ─── */}
                  <LabelList
                    dataKey={p.key}
                    content={(props) => {
                      const { x, y, payload } = props;

                      const actualValue =
                        Number(payload?._raw?.[p.key]) || 0;

                      if (actualValue <= 0) return null;

                      return (
                        <text
                          x={x}
                          y={y}
                          dy={-6}
                          textAnchor="middle"
                          fill={p.color}
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                          }}
                        >
                          {actualValue}
                        </text>
                      );
                    }}
                  />
                </Line>
              ))}
            </LineChart>
          </ResponsiveContainer>
          <LegendRow items={products.map(p => ({ label: p.name, color: p.color }))} />
        </>
      ) : (
        <div className="h-40 flex items-center justify-center text-slate-400 text-xs">No product sales logs available for this period.</div>
      )}
    </div>
  );
}

// ── Summary Cards ──────────────────────────────────────────────────────────
function SummaryCards( {navigate}) {
  const { summary, onboardingData, salesData } = useDashboardData();

  const seatsBookedPct = useMemo(() => {
    if (!summary.total_retailers) return 0;
    return Math.round((summary.active_retailers / summary.total_retailers) * 100);
  }, [summary]);

  const avgOnb = useMemo(() => {
    const total = onboardingData.reduce((s, r) => s + r.count, 0);
    return Math.round(total / (onboardingData.length || 1));
  }, [onboardingData]);

  const avgWSold = useMemo(() => {
    const total = salesData.reduce((s, r) => s + r.sales, 0);
    return Math.round(total / (salesData.length || 1));
  }, [salesData]);

  const avgWAct = avgOnb ? Math.round(avgOnb * 1.2) : 0; 
  const todayWAct = Math.round(summary.todays_sales * 0.8);

  const todayComps = [
    { label: "Activations", today: summary.todays_onboarded,     avg: avgWAct,   color: "#2ECC71" },
    { label: "Onboarded",   today: summary.todays_onboarded, avg: avgOnb,   color: "#F59E0B" },
    { label: "W. Sold",     today: summary.todays_sales,     avg: avgWSold, color: "#6C63FF" },
  ];

  return (
    <div className="mb-5 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-3">
          <div className="bg-white rounded-2xl border border-slate-100 flex flex-col items-center justify-center py-5 gap-2">
            <Donut pct={seatsBookedPct} />
            <p className="text-xs text-slate-400">{summary.active_retailers} Retailers are Active </p>
          </div>
          <GoalBar label="Warranty Sold" value={summary.total_sales} today={summary.todays_sales} />
        </div>
        <div className="flex flex-col gap-3">
          <StatCard
            icon="ti-building-store"
            iconBg="#FFF0E6"
            iconColor="#FF8C42"
            value={summary.total_retailers}
            label="Total Retailers"
            arrow
            onClick={() =>
              navigate("/partners", {
                state: { activeTab: "arp" },
              })
            }
          />

          <StatCard
            icon="ti-shield-check"
            iconBg="#EEF9F3"
            iconColor="#2ECC71"
            value={summary.total_warranty_sold}
            label="Warranty Activations"
            arrow
            onClick={() => navigate("/admwrnty")}
          />

          <StatCard
            icon="ti-users-group"
            iconBg="#EEF3FF"
            iconColor="#6C63FF"
            value={summary.total_channel_partners}
            label="Channel Partners"
            arrow
            onClick={() =>
              navigate("/partners", {
                state: { activeTab: "cp" },
              })
            }
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard
          icon="ti-user-plus"
          iconBg="#FFF5E6"
          iconColor="#F59E0B"
          value={summary.total_onboarded}
          label="Onboarded"
        />

        <StatCard
          icon="ti-credit-card"
          iconBg="#FEF0F5"
          iconColor="#EC4899"
          value={summary.total_subscription}
          label="Subscriptions"
        />

        <div className="bg-white rounded-2xl px-4 py-3 border border-slate-100 flex flex-col gap-2">
          <p className="text-[10px] text-slate-400 font-medium">Today</p>

          {todayComps.map((t) => (
            <div
              key={t.label}
              className="flex items-center justify-between"
            >
              <span className="flex items-center gap-1.5 text-[11px] text-slate-500 truncate">
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: t.color }}
                />
                {t.label}
              </span>

              <span
                className={`text-xs font-semibold whitespace-nowrap ${
                  t.today >= t.avg
                    ? "text-emerald-600"
                    : "text-red-500"
                }`}
              >
                {t.today >= t.avg ? "↑" : "↓"} {t.today}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Root Framework System Container ──────────────────────────────────────
export default function AdminDashboardCharts() {
  const firstDayOfCurrentMonth = new Date(TODAY.getFullYear(), TODAY.getMonth(), 1);
  
  const defaultFrom = toDateStr(firstDayOfCurrentMonth); 
  const defaultTo   = toDateStr(TODAY);                  

  const [from, setFrom] = useState(defaultFrom);
  const [to,   setTo  ] = useState(defaultTo);

  const [summary, setSummary] = useState({
    total_retailers: 0, active_retailers: 0, total_channel_partners: 0,
    total_warranty_sold: 0, total_sales: 0, todays_sales: 0,
    total_onboarded: 0, todays_onboarded: 0, total_subscription: 0, todays_subscription: 0
  });
  const [onboardingData, setOnboardingData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [leadData, setLeadData] = useState([]);
  const [productData, setProductData] = useState([]);

  const [loadingOnboarding, setLoadingOnboarding] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);

  const [reportType, setReportType] = useState("products");
  const [productTotals, setProductTotals] = useState({});

  const [leadSummary, setLeadSummary] = useState({
  created: 0,
  in_progress: 0,
  won: 0,
  reject: 0,
  conversion_rate: 0,
});

const navigate = useNavigate();

  useEffect(() => {
    async function fetchSummary() {
      try {
        const res = await axios.get(`${BASE_URL}warranty/dashboard/summary`, { params: getParams() });
        if (res.data.data) setSummary(res.data.data);
      } catch (err) {
        console.error("Summary API error:", err);
      }
    }
    fetchSummary();
  }, []);

  const fetchOnboarding = async (start_date, end_date) => {
    try {
      setLoadingOnboarding(true);
      const params = { ...getParams(), start_date, end_date };
      const res = await axios.get(`${BASE_URL}warranty/onboarding-stats`, { params });
      if (res.data) {
        const formatted = res.data.map((item) => ({
          date: item.date,
          day: new Date(item.date).getDate(),
          count: item.count,
        }));
        setOnboardingData(formatted);
      }
    } catch (err) {
      console.error("Onboarding API error:", err);
    } finally {
      setLoadingOnboarding(false);
    }
  };

  // const fetchProductSales = async (start_date, end_date) => {
  //   try {
  //     setLoadingProduct(true);
  //     const params = { ...getParams(), start_date, end_date };
  //     const res = await axios.get(`${BASE_URL}warranty/product-sales-report`, { params });

  //     if (res.data.products[0]) {
  //       const normalizeFullData = (data, sDate, eDate) => {
  //         const keys = new Set();
  //         data.forEach((d) => {
  //           Object.keys(d).forEach((k) => {
  //             if (k !== "date") keys.add(k);
  //           });
  //         });

  //         const start = new Date(sDate);
  //         const end = new Date(eDate);
  //         const result = [];

  //         for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
  //           const dateStr = d.toISOString().split("T")[0];
  //           const found = data.find((item) => item.date === dateStr);
  //           const row = { date: dateStr };

  //           keys.forEach((k) => {
  //             row[k] = found ? found[k] || 0 : 0;
  //           });
  //           result.push(row);
  //         }
  //         return result;
  //       };

  //       setProductData(normalizeFullData(res.data.products[0], start_date, end_date));
  //     }
  //   } catch (err) {
  //     console.error("Product API error:", err);
  //   } finally {
  //     setLoadingProduct(false);
  //   }
  // };

  const fetchProductSales = async (start_date, end_date) => {
  try {
    setLoadingProduct(true);

    const params = { ...getParams(), start_date, end_date };

    const res = await axios.get(
      `${BASE_URL}warranty/product-sales-report`,
      { params }
    );

    const normalizeFullData = (data, sDate, eDate) => {
      const keys = new Set();

      data.forEach((d) => {
        Object.keys(d).forEach((k) => {
          if (k !== "date") keys.add(k);
        });
      });

      const start = new Date(sDate);
      const end = new Date(eDate);

      const result = [];

      for (
        let d = new Date(start);
        d <= end;
        d.setDate(d.getDate() + 1)
      ) {
        const dateStr = d.toISOString().split("T")[0];
        const found = data.find((item) => item.date === dateStr);
        const row = { date: dateStr };

        keys.forEach((k) => {
          row[k] = found?.[k]?.count || 0; // show count
        });

        result.push(row);
      }

      return result;
    };

    // Select dataset based on dropdown
    const selectedData =
      reportType === "products"
        ? res.data.products
        : res.data.product_types;

    const selectedTotals =
      reportType === "products"
        ? res.data.product_totals
        : res.data.product_type_totals;

    setProductData(
      normalizeFullData(selectedData, start_date, end_date)
    );

    setProductTotals(selectedTotals);
  } catch (err) {
    console.error("Product API error:", err);
  } finally {
    setLoadingProduct(false);
  }
};

  useEffect(() => {
    async function fetchDashboardMetrics() {
      const params = { ...getParams(), start_date: from, end_date: to };
      
      try {
        const res = await axios.get(`${BASE_URL}warranty/dashboard/retailer-status`, { params });
        if (res.data?.data) {
          setStatusData(res.data.data.map(item => ({
            date: item.date,
            day: new Date(item.date).getDate(),
            active: item.active,
            using: item.using,
            notUsing: item.not_using, 
            pending: item.pending,
            inactive: item.inactive
          })));
        }
      } catch (e) { console.error("Status breakdown parsing failure: ", e); }

      try {
        const res = await axios.get(`${BASE_URL}warranty/dashboard/warranty-sales`, { params });
        if (res.data?.data) {
          setSalesData(res.data.data.map(item => ({
            date: item.date,
            day: new Date(item.date).getDate(),
            sales: item.sales
          })));
        }
      } catch (e) { console.error("Sales API processing error: ", e); }

      try {
  const res = await axios.get(
    `${BASE_URL}warranty/dashboard/leads`,
    { params }
  );

  if (res.data?.data) {
    setLeadData(
      res.data.data.map((item) => ({
        date: item.date,
        day: new Date(item.date).getDate(),
        created: item.created,
        won: item.won,
        inProgress: Number(item.in_progress || 0),
        reject: item.reject,
      }))
    );
  }

  if (res.data?.summary) {
    setLeadSummary(res.data.summary);
  }
} catch (e) {
  console.error("Leads metrics fetch anomaly:", e);
}
    }

    fetchOnboarding(from, to);
    fetchProductSales(from, to); 
    fetchDashboardMetrics();
  }, [from, to, reportType]);

  function handleRangeChange(f, t) { setFrom(f); setTo(t); }

  return (
    <DashboardDataCtx.Provider value={{ 
      from, to, summary, onboardingData, statusData, salesData, leadData,leadSummary, productData, productTotals, loadingOnboarding, loadingProduct 
    }}>
      <DateRangePicker from={from} to={to} onChange={handleRangeChange} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SummaryCards navigate={navigate} />
        <OnboardingChart />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <RetailerAnalysisChart />
        <WarrantySalesChart />
        <LeadChart />
        <ProductWarrantyChart reportType={reportType} setReportType={setReportType} />
      </div>
    </DashboardDataCtx.Provider>
  );
}

// import { useState, useMemo, createContext, useContext, useEffect } from "react";
// import axios from "axios";
// import {
//   BarChart, Bar, LineChart, Line, AreaChart, Area,
//   XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
// } from "recharts";
// import { BASE_URL } from "../../../config/api";

// // ── Base API Configurations ────────────────────────────────────────────────
// function getParams() {
//   return {};
// }

// // ── Date helpers ───────────────────────────────────────────────────────────
// function toDateStr(date) {
//   return date.toISOString().slice(0, 10); // "YYYY-MM-DD"
// }
// function offsetDate(base, days) {
//   const d = new Date(base);
//   d.setDate(d.getDate() + days);
//   return d;
// }

// const TODAY = new Date();

// // ── Date Range Context ─────────────────────────────────────────────────────
// const DashboardDataCtx = createContext(null);
// function useDashboardData() { return useContext(DashboardDataCtx); }

// // ── Date Range Picker ──────────────────────────────────────────────────────
// const PRESETS = [
//   { label: "7d",   days: 7 },
//   { label: "14d", days: 14 },
//   { label: "30d", days: 30 },
//   { label: "60d", days: 60 },
//   { label: "90d", days: 90 },
// ];

// export function DateRangePicker({ from, to, onChange }) {
//   // Default to empty string so preset badges aren't falsely highlighted on current month default
//   const [activePreset, setActivePreset] = useState("");

//   function applyPreset(days, label) {
//     setActivePreset(label);
//     const f = toDateStr(offsetDate(TODAY, -(days - 1)));
//     const t = toDateStr(TODAY);
//     onChange(f, t);
//   }

//   function handleFrom(e) {
//     setActivePreset("custom");
//     onChange(e.target.value, to);
//   }
//   function handleTo(e) {
//     setActivePreset("custom");
//     onChange(from, e.target.value);
//   }

//   const dayCount = useMemo(() => {
//     const diff = (new Date(to) - new Date(from)) / 86400000 + 1;
//     return Math.max(0, Math.round(diff));
//   }, [from, to]);

//   return (
//     <div className="bg-white rounded-2xl border border-slate-100 px-4 py-3 flex flex-wrap items-center gap-3 mb-4">
//       <div className="flex items-center gap-1.5 text-xs text-slate-400">
//         <i className="ti ti-calendar text-sm" />
//         <span>Date range</span>
//       </div>

//       <div className="flex gap-1.5 flex-wrap">
//         {PRESETS.map(p => (
//           <button
//             key={p.label}
//             onClick={() => applyPreset(p.days, p.label)}
//             className={`text-xs px-3 py-1 rounded-full border transition-all font-medium ${
//               activePreset === p.label
//                 ? "bg-[#6C63FF] text-white border-[#6C63FF]"
//                 : "bg-white text-slate-500 border-slate-200 hover:border-[#6C63FF] hover:text-[#6C63FF]"
//             }`}
//           >
//             Last {p.label}
//           </button>
//         ))}
//       </div>

//       <div className="w-px h-5 bg-slate-100" />

//       <div className="flex items-center gap-2">
//         <input
//           type="date"
//           value={from}
//           max={to}
//           onChange={handleFrom}
//           className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 focus:outline-none focus:border-[#6C63FF]"
//         />
//         <span className="text-slate-300 text-xs">→</span>
//         <input
//           type="date"
//           value={to}
//           min={from}
//           max={toDateStr(TODAY)}
//           onChange={handleTo}
//           className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 focus:outline-none focus:border-[#6C63FF]"
//         />
//       </div>

//       <span className="ml-auto text-[11px] font-medium px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600">
//         {dayCount} day{dayCount !== 1 ? "s" : ""} selected
//       </span>
//     </div>
//   );
// }

// // ── Shared sub-components ─────────────────────────────────────────────────
// function MetricCards({ items }) {
//   return (
//     <div className="flex flex-wrap gap-2 mb-3">
//       {items.map((m) => (
//         <div key={m.label} className="flex-1 min-w-[100px] bg-slate-50 rounded-xl px-3 py-2.5">
//           <p className="text-[10px] text-slate-400 mb-0.5 uppercase tracking-wide font-medium">{m.label}</p>
//           <p className="text-base font-semibold" style={{ color: m.color }}>{m.value.toLocaleString()}</p>
//         </div>
//       ))}
//     </div>
//   );
// }

// // Adjusted alignment condition to match right corner specification for lists/legends if needed
// function LegendRow({ items }) {
//   return (
//     <div className="flex flex-wrap gap-3 mt-2">
//       {items.map((item) => (
//         <span key={item.label} className="flex items-center gap-1.5 text-[11px] text-slate-500">
//           <span className="w-2 h-2 rounded-sm inline-block flex-shrink-0" style={{ background: item.color }} />
//           {item.label}
//         </span>
//       ))}
//     </div>
//   );
// }

// function CustomTooltip({ active, payload, label }) {
//   if (!active || !payload?.length) return null;
//   const seen = new Set();
//   const items = payload.filter(p => { if (seen.has(p.dataKey)) return false; seen.add(p.dataKey); return true; });
//   return (
//     <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-lg text-xs min-w-[120px]">
//       <p className="text-slate-400 font-medium mb-1.5 text-[10px] uppercase tracking-wide">{label}</p>
//       {items.map((p, i) => (
//         <div key={i} className="flex justify-between gap-4 mb-0.5">
//           <span className="flex items-center gap-1.5 text-slate-500">
//             <span className="w-1.5 h-1.5 rounded-sm inline-block" style={{ background: p.color || p.stroke }} />
//             {p.name}
//           </span>
//           <span className="font-semibold text-slate-800">{p.value.toLocaleString()}</span>
//         </div>
//       ))}
//     </div>
//   );
// }

// const axisProps = { axisLine: false, tickLine: false, tick: { fontSize: 9, fill: "#94a3b8" } };
// function xInterval(len) { return len > 20 ? Math.floor(len / 8) : 1; }

// function Donut({ pct, color = "#6C63FF", size = 88 }) {
//   const r = size * 0.4, circ = 2 * Math.PI * r;
//   const percentage = isNaN(pct) ? 0 : pct;
//   return (
//     <div className="relative" style={{ width: size, height: size }}>
//       <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
//         <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f0eeff" strokeWidth={9} />
//         <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={9}
//           strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - percentage / 100)} />
//       </svg>
//       <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold" style={{ color }}>{percentage}%</div>
//     </div>
//   );
// }

// function StatCard({ icon, iconBg, iconColor, value, label, arrow }) {
//   return (
//     <div className="bg-white rounded-2xl flex items-center gap-3 px-4 py-4 border border-slate-100">
//       <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg" style={{ background: iconBg, color: iconColor }}>
//         <i className={`ti ${icon}`} />
//       </div>
//       <div className="flex-1 min-w-0">
//         <p className="text-lg font-semibold text-slate-800 leading-none">{typeof value === "number" ? value.toLocaleString() : value || 0}</p>
//         <p className="text-[11px] text-slate-400 mt-0.5">{label}</p>
//       </div>
//       {arrow && <span className="text-slate-300 text-base">›</span>}
//     </div>
//   );
// }

// function GoalBar({ label, value, target, color = "#6C63FF" }) {
//   const pct = target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0;
//   return (
//     <div className="bg-white rounded-2xl px-4 py-3.5 border border-slate-100">
//       <div className="flex items-center justify-between mb-2">
//         <span className="text-sm font-semibold text-slate-700">{label}</span>
//         <span className="text-sm font-semibold" style={{ color }}>{pct}%</span>
//       </div>
//       <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
//         <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
//       </div>
//       <p className="text-[10px] text-slate-400 mt-1.5">{value.toLocaleString()} sold · target {target.toLocaleString()}</p>
//     </div>
//   );
// }

// // ── 1. Retailer Onboarding ─────────────────────────────────────────────────
// export function OnboardingChart() {
//   const { onboardingData } = useDashboardData();
//   const total = onboardingData.reduce((s, r) => s + r.count, 0);
//   const peak = onboardingData.reduce((a, b) => (b.count > a.count ? b : a), onboardingData[0] || {});
  
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Retailer Onboarding</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-4">
//         Total: <span className="text-blue-600 font-semibold">{total}</span> · Peak:{" "}
//         <span className="text-blue-800 font-semibold">{peak.count || 0}</span>
//       </p>
//       <ResponsiveContainer width="100%" height={160}>
//         <BarChart data={onboardingData} barSize={onboardingData.length > 20 ? 6 : 10} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//           <CartesianGrid vertical={false} stroke="#f1f5f9" />
//           <XAxis dataKey="day" {...axisProps} interval={xInterval(onboardingData.length)} />
//           <YAxis {...axisProps} allowDecimals={false} />
//           <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
//           <Bar dataKey="count" name="Onboarded" radius={[3, 3, 0, 0]}>
//             {onboardingData.map((e, i) => (
//               <Cell key={i} fill={e.count === 0 ? "#dbeafe" : e.count === peak.count ? "#1d4ed8" : "#3b82f6"} />
//             ))}
//           </Bar>
//         </BarChart>
//       </ResponsiveContainer>
//       <LegendRow items={[{ label: "Onboarded", color: "#3b82f6" }, { label: "No activity", color: "#dbeafe" }, { label: "Peak day", color: "#1d4ed8" }]} />
//     </div>
//   );
// }

// // ── 2. Retailer Status Analysis ────────────────────────────────────────────
// const RETAILER_SERIES = [
//   { key: "active",   name: "Active",          color: "#22c55e" },
//   { key: "using",    name: "Using",           color: "#3b82f6" },
//   { key: "notUsing", name: "Not using",       color: "#f59e0b" },
//   { key: "pending",  name: "Pending activation", color: "#a855f7" },
//   { key: "inactive", name: "Inactive",        color: "#ef4444" },
// ];

// export function RetailerAnalysisChart() {
//   const { statusData } = useDashboardData();
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Retailer Status Analysis</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-4">Daywise breakdown by retailer status</p>
//       <ResponsiveContainer width="100%" height={160}>
//         <BarChart data={statusData} barSize={statusData.length > 20 ? 4 : 8} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//           <CartesianGrid vertical={false} stroke="#f1f5f9" />
//           <XAxis dataKey="day" {...axisProps} interval={xInterval(statusData.length)} />
//           <YAxis {...axisProps} allowDecimals={false} />
//           <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
//           {RETAILER_SERIES.map((s, i) => (
//             <Bar key={s.key} dataKey={s.key} name={s.name} stackId="a" fill={s.color}
//               radius={i === RETAILER_SERIES.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
//           ))}
//         </BarChart>
//       </ResponsiveContainer>
//       <LegendRow items={RETAILER_SERIES.map(s => ({ label: s.name, color: s.color }))} />
//     </div>
//   );
// }

// // ── 3. Warranty Sales ──────────────────────────────────────────────────────
// export function WarrantySalesChart() {
//   const { salesData } = useDashboardData();
//   const total = salesData.reduce((s, r) => s + r.sales, 0);
//   const avg = Math.round(total / (salesData.length || 1));
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Warranty Sales</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-4">
//         Total: <span className="text-blue-600 font-semibold">{total.toLocaleString()}</span> · Daily avg:{" "}
//         <span className="text-emerald-600 font-semibold">{avg}</span>
//       </p>
//       <ResponsiveContainer width="100%" height={170}>
//         <AreaChart data={salesData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//           <defs>
//             <linearGradient id="wsGrad" x1="0" y1="0" x2="0" y2="1">
//               <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
//               <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
//             </linearGradient>
//           </defs>
//           <CartesianGrid vertical={false} stroke="#f1f5f9" />
//           <XAxis dataKey="day" {...axisProps} interval={xInterval(salesData.length)} />
//           <YAxis {...axisProps} allowDecimals={false} />
//           <Tooltip content={<CustomTooltip />} />
//           <Area dataKey="sales" name="Warranty sales" stroke="#3b82f6" strokeWidth={2}
//             fill="url(#wsGrad)" dot={false} activeDot={{ r: 4, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }} />
//         </AreaChart>
//       </ResponsiveContainer>
//       <LegendRow items={[{ label: "Warranty sales", color: "#3b82f6" }]} />
//     </div>
//   );
// }

// // ── 4. Lead Chart ──────────────────────────────────────────────────────────
// export function LeadChart() {
//   const { leadData } = useDashboardData();
//   const totCreated = leadData.reduce((s, r) => s + r.created, 0);
//   const totWon     = leadData.reduce((s, r) => s + r.won, 0);
//   const rate = totCreated ? Math.round((totWon / totCreated) * 100) : 0;
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Lead Created vs Won</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-3">Daywise lead pipeline and conversion</p>
//       <MetricCards items={[
//         { label: "Total leads", value: totCreated, color: "#818cf8" },
//         { label: "Won",         value: totWon,     color: "#34d399" },
//         { label: "Conversion",  value: `${rate}%`, color: "#a855f7" },
//       ]} />
//       <ResponsiveContainer width="100%" height={130}>
//         <BarChart data={leadData} barSize={leadData.length > 20 ? 4 : 8} barGap={2} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//           <CartesianGrid vertical={false} stroke="#f1f5f9" />
//           <XAxis dataKey="day" {...axisProps} interval={xInterval(leadData.length)} />
//           <YAxis {...axisProps} allowDecimals={false} />
//           <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
//           <Bar dataKey="created" name="Created" fill="#818cf8" radius={[3, 3, 0, 0]} />
//           <Bar dataKey="won"      name="Won"     fill="#34d399" radius={[3, 3, 0, 0]} />
//         </BarChart>
//       </ResponsiveContainer>
//       <LegendRow items={[{ label: "Created", color: "#818cf8" }, { label: "Won", color: "#34d399" }]} />
//     </div>
//   );
// }

// // ── 5. Product Warranty Sales ─────────────────────────────────────────────
// const PALETTE = ["#818cf8", "#34d399", "#fb923c", "#38bdf8", "#a855f7", "#ec4899", "#f59e0b"];

// export function ProductWarrantyChart() {
//   const { productData } = useDashboardData();

//   const { products, chartData } = useMemo(() => {
//   const keys = new Set();

//   productData.forEach((d) => {
//     Object.keys(d).forEach((k) => {
//       if (k !== "date" && k !== "day") {
//         keys.add(k);
//       }
//     });
//   });

//   const productKeys = Array.from(keys);

//   const lastValues = {};

//   const formatted = productData.map((item) => {
//     const row = {
//       ...item,
//       day: new Date(item.date).getDate(),
//     };

//     productKeys.forEach((key) => {
//       const currentValue = Number(item[key]) || 0;

//       if (currentValue > 0) {
//         lastValues[key] = currentValue;
//       } else if (lastValues[key] !== undefined) {
//         row[key] = lastValues[key];
//       }
//     });

//     return row;
//   });

//   const dynamicProducts = productKeys.map((key, index) => ({
//     key,
//     name: key.charAt(0).toUpperCase() + key.slice(1),
//     color: PALETTE[index % PALETTE.length],
//   }));

//   return {
//     products: dynamicProducts,
//     chartData: formatted,
//   };
// }, [productData]);

//   const totals = useMemo(() => {
//     return products.reduce((acc, p) => {
//       acc[p.key] = chartData.reduce((s, r) => s + (Number(r[p.key]) || 0), 0);
//       return acc;
//     }, {});
//   }, [products, chartData]);

//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Product Warranty Sales</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-3">Daywise product-wise warranty sales trend</p>
      
//       {products.length > 0 ? (
//         <>
//           <MetricCards items={products.map(p => ({ label: p.name, value: totals[p.key] || 0, color: p.color }))} />
//           <ResponsiveContainer width="100%" height={130}>
//             <LineChart data={chartData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//               <CartesianGrid vertical={false} stroke="#f1f5f9" />
//               <XAxis dataKey="day" {...axisProps} interval={xInterval(chartData.length)} />
//               <YAxis {...axisProps} allowDecimals={false} />
//               <Tooltip content={<CustomTooltip />} />
//               {/* {products.map((p, i) => (
//                 <Line key={p.key} dataKey={p.key} name={p.name} stroke={p.color} strokeWidth={2}
//                   dot={false} strokeDasharray={i > 3 ? "4 3" : undefined}
//                   activeDot={{ r: 4, fill: p.color, stroke: "#fff", strokeWidth: 2 }} />
//               ))} */}

//               {products.map((p, i) => (
//                 <Line
//                   key={p.key}
//                   dataKey={p.key}
//                   name={p.name}
//                   stroke={p.color}
//                   strokeWidth={2}
//                   dot={false}
//                   strokeDasharray={i > 1 ? "4 3" : undefined}
//                   activeDot={{
//                     r: 4,
//                     fill: p.color,
//                     stroke: "#fff",
//                     strokeWidth: 2,
//                   }}
//                 />
//               ))}
//             </LineChart>
//           </ResponsiveContainer>
//           <LegendRow items={products.map(p => ({ label: p.name, color: p.color }))} />
//         </>
//       ) : (
//         <div className="h-40 flex items-center justify-center text-slate-400 text-xs">No product sales logs available for this period.</div>
//       )}
//     </div>
//   );
// }

// // ── Summary Cards ──────────────────────────────────────────────────────────
// function SummaryCards() {
//   const { summary, onboardingData, salesData } = useDashboardData();

//   const seatsBookedPct = useMemo(() => {
//     if (!summary.total_retailers) return 0;
//     return Math.round((summary.active_retailers / summary.total_retailers) * 100);
//   }, [summary]);

//   const avgOnb = useMemo(() => {
//     const total = onboardingData.reduce((s, r) => s + r.count, 0);
//     return Math.round(total / (onboardingData.length || 1));
//   }, [onboardingData]);

//   const avgWSold = useMemo(() => {
//     const total = salesData.reduce((s, r) => s + r.sales, 0);
//     return Math.round(total / (salesData.length || 1));
//   }, [salesData]);

//   const avgWAct = avgOnb ? Math.round(avgOnb * 1.2) : 0; 
//   const todayWAct = Math.round(summary.todays_sales * 0.8);

//   const todayComps = [
//     { label: "Activations", today: todayWAct,     avg: avgWAct,   color: "#2ECC71" },
//     { label: "Onboarded",   today: summary.todays_onboarded, avg: avgOnb,   color: "#F59E0B" },
//     { label: "W. Sold",     today: summary.todays_sales,     avg: avgWSold, color: "#6C63FF" },
//   ];

//   return (
//     <div className="mb-5 space-y-3">
//       <div className="grid grid-cols-2 gap-3">
//         <div className="flex flex-col gap-3">
//           <div className="bg-white rounded-2xl border border-slate-100 flex flex-col items-center justify-center py-5 gap-2">
//             <Donut pct={seatsBookedPct} />
//             <p className="text-xs text-slate-400">Active Retailers</p>
//           </div>
//           <GoalBar label="Warranty Sold" value={summary.total_warranty_sold} target={1500} />
//         </div>
//         <div className="flex flex-col gap-3">
//           <StatCard icon="ti-building-store" iconBg="#FFF0E6" iconColor="#FF8C42" value={summary.total_retailers} label="Total Retailers" arrow />
//           <StatCard icon="ti-shield-check"   iconBg="#EEF9F3" iconColor="#2ECC71" value={summary.total_sales}       label="Warranty Activations" arrow />
//           <StatCard icon="ti-users-group"    iconBg="#EEF3FF" iconColor="#6C63FF" value={summary.total_channel_partners} label="Channel Partners" arrow />
//         </div>
//       </div>
//       <div className="grid grid-cols-3 gap-3">
//         <StatCard icon="ti-user-plus"   iconBg="#FFF5E6" iconColor="#F59E0B" value={summary.total_onboarded}  label={`Onboarded`} />
//         <StatCard icon="ti-credit-card" iconBg="#FEF0F5" iconColor="#EC4899" value={summary.total_subscription} label={`Subscriptions`} />
//         <div className="bg-white rounded-2xl px-4 py-3 border border-slate-100 flex flex-col gap-2">
//           <p className="text-[10px] text-slate-400 font-medium">Today vs avg</p>
//           {todayComps.map(t => (
//             <div key={t.label} className="flex items-center justify-between">
//               <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
//                 <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
//                 {t.label}
//               </span>
//               <span className={`text-xs font-semibold ${t.today >= t.avg ? "text-emerald-600" : "text-red-500"}`}>
//                 {t.today >= t.avg ? "↑" : "↓"} {t.today}
//               </span>
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// }

// // ── Root Framework System Container ──────────────────────────────────────
// export default function AdminDashboardCharts() {
//   // 1. Configure default range dynamically targeting the first day of the current month
//   const firstDayOfCurrentMonth = new Date(TODAY.getFullYear(), TODAY.getMonth(), 1);
  
//   const defaultFrom = toDateStr(firstDayOfCurrentMonth); 
//   const defaultTo   = toDateStr(TODAY);                  

//   const [from, setFrom] = useState(defaultFrom);
//   const [to,   setTo  ] = useState(defaultTo);

//   // API Data Hooks Matrix
//   const [summary, setSummary] = useState({
//     total_retailers: 0, active_retailers: 0, total_channel_partners: 0,
//     total_warranty_sold: 0, total_sales: 0, todays_sales: 0,
//     total_onboarded: 0, todays_onboarded: 0, total_subscription: 0, todays_subscription: 0
//   });
//   const [onboardingData, setOnboardingData] = useState([]);
//   const [statusData, setStatusData] = useState([]);
//   const [salesData, setSalesData] = useState([]);
//   const [leadData, setLeadData] = useState([]);
//   const [productData, setProductData] = useState([]);

//   // Load flags
//   const [loadingOnboarding, setLoadingOnboarding] = useState(false);
//   const [loadingProduct, setLoadingProduct] = useState(false);

//   // 1. Fetch Summary Stats
//   useEffect(() => {
//     async function fetchSummary() {
//       try {
//         const res = await axios.get(`${BASE_URL}dashboard/summary`, { params: getParams() });
//         if (res.data) setSummary(res.data);
//       } catch (err) {
//         console.error("Summary API error:", err);
//       }
//     }
//     fetchSummary();
//   }, []);

//   // 2. Fetch Onboarding Data Lineages
//   const fetchOnboarding = async (start_date, end_date) => {
//     try {
//       setLoadingOnboarding(true);
//       const params = { ...getParams(), start_date, end_date };
//       const res = await axios.get(`${BASE_URL}warranty/onboarding-stats`, { params });
//       if (res.data) {
//         const formatted = res.data.map((item) => ({
//           date: item.date,
//           day: new Date(item.date).getDate(),
//           count: item.count,
//         }));
//         setOnboardingData(formatted);
//       }
//     } catch (err) {
//       console.error("Onboarding API error:", err);
//     } finally {
//       setLoadingOnboarding(false);
//     }
//   };

//   // 3. Fetch Product Sales Report
//   const fetchProductSales = async (start_date, end_date) => {
//     try {
//       setLoadingProduct(true);
//       const params = { ...getParams(), start_date, end_date };
//       const res = await axios.get(`${BASE_URL}warranty/product-sales-report`, { params });

//       if (res.data) {
//         const normalizeFullData = (data, sDate, eDate) => {
//           const keys = new Set();
//           data.forEach((d) => {
//             Object.keys(d).forEach((k) => {
//               if (k !== "date") keys.add(k);
//             });
//           });

//           const start = new Date(sDate);
//           const end = new Date(eDate);
//           const result = [];

//           for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
//             const dateStr = d.toISOString().split("T")[0];
//             const found = data.find((item) => item.date === dateStr);
//             const row = { date: dateStr };

//             keys.forEach((k) => {
//               row[k] = found ? found[k] || 0 : 0;
//             });
//             result.push(row);
//           }
//           return result;
//         };

//         setProductData(normalizeFullData(res.data, start_date, end_date));
//       }
//     } catch (err) {
//       console.error("Product API error:", err);
//     } finally {
//       setLoadingProduct(false);
//     }
//   };

//   // 4. Fetch Context Metrics Breakdown Pipeline
//   useEffect(() => {
//     async function fetchDashboardMetrics() {
//       const params = { ...getParams(), start_date: from, end_date: to };
      
//       // Status breakdown mapping 
//       try {
//         const res = await axios.get(`${BASE_URL}dashboard/retailer-status`, { params });
//         if (res.data?.data) {
//           setStatusData(res.data.data.map(item => ({
//             date: item.date,
//             day: new Date(item.date).getDate(),
//             active: item.active,
//             using: item.using,
//             notUsing: item.not_using, 
//             pending: item.pending,
//             inactive: item.inactive
//           })));
//         }
//       } catch (e) { console.error("Status breakdown parsing failure: ", e); }

//       // Sales chart array mapping
//       try {
//         const res = await axios.get(`${BASE_URL}dashboard/warranty-sales`, { params });
//         if (res.data?.data) {
//           setSalesData(res.data.data.map(item => ({
//             date: item.date,
//             day: new Date(item.date).getDate(),
//             sales: item.sales
//           })));
//         }
//       } catch (e) { console.error("Sales API processing error: ", e); }

//       // Pipeline leads generation sequence processing
//       try {
//         const res = await axios.get(`${BASE_URL}dashboard/leads`, { params });
//         if (res.data?.data) {
//           setLeadData(res.data.data.map(item => ({
//             date: item.date,
//             day: new Date(item.date).getDate(),
//             created: item.created,
//             won: item.won,
//             inProgress: parseInt(item.in_progress || 0),
//             reject: item.reject
//           })));
//         }
//       } catch (e) { console.error("Leads metrics fetch anomaly: ", e); }
//     }

//     fetchOnboarding(from, to);
//     fetchProductSales(from, to); 
//     fetchDashboardMetrics();
//   }, [from, to]);

//   function handleRangeChange(f, t) { setFrom(f); setTo(t); }

//   return (
//     <DashboardDataCtx.Provider value={{ 
//       from, to, summary, onboardingData, statusData, salesData, leadData, productData, loadingOnboarding, loadingProduct 
//     }}>
//       <DateRangePicker from={from} to={to} onChange={handleRangeChange} />

//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
//         <SummaryCards />
//         <OnboardingChart />
//       </div>

//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
//         <RetailerAnalysisChart />
//         <WarrantySalesChart />
//         <LeadChart />
//         <ProductWarrantyChart />
//       </div>
//     </DashboardDataCtx.Provider>
//   );
// }

// import { useState, useMemo, createContext, useContext } from "react";
// import {
//   BarChart, Bar, LineChart, Line, AreaChart, Area,
//   XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
// } from "recharts";

// // ── Date helpers ───────────────────────────────────────────────────────────
// function toDateStr(date) {
//   return date.toISOString().slice(0, 10); // "YYYY-MM-DD"
// }
// function offsetDate(base, days) {
//   const d = new Date(base);
//   d.setDate(d.getDate() + days);
//   return d;
// }

// // ── Generate mock daily data keyed by date string ─────────────────────────
// const TODAY = new Date(2025, 4, 30); // May 30 2025

// function genDays(n = 90) {
//   return Array.from({ length: n }, (_, i) => {
//     const d = offsetDate(TODAY, -(n - 1 - i));
//     return { date: toDateStr(d), day: d.getDate(), dateObj: d };
//   });
// }
// const ALL_DAYS = genDays(90);

// const onboardingData      = ALL_DAYS.map(d => ({ ...d, count: Math.round(Math.random() * 7) }));
// const retailerStatus      = ALL_DAYS.map(d => ({ ...d, active: Math.round(8 + Math.random() * 5), using: Math.round(5 + Math.random() * 4), notUsing: Math.round(2 + Math.random() * 3), pending: Math.round(1 + Math.random() * 2), inactive: Math.round(Math.random() * 2) }));
// const warrantySales       = ALL_DAYS.map(d => ({ ...d, sales: Math.round(20 + Math.random() * 30) }));
// const invoiceData         = ALL_DAYS.map(d => ({ ...d, created: Math.round(10 + Math.random() * 12), paid: Math.round(6 + Math.random() * 10) }));
// const leadData            = ALL_DAYS.map(d => ({ ...d, created: Math.round(8 + Math.random() * 10), won: Math.round(3 + Math.random() * 6) }));
// const productSales        = ALL_DAYS.map(d => ({ ...d, mobile: Math.round(4 + Math.random() * 8), tv: Math.round(2 + Math.random() * 5), ac: Math.round(1 + Math.random() * 4), laptop: Math.round(3 + Math.random() * 6) }));
// const subscriptionData    = ALL_DAYS.map(d => ({ ...d, count: Math.round(5 + Math.random() * 9) }));
// const warrantyActData     = ALL_DAYS.map(d => ({ ...d, count: Math.round(8 + Math.random() * 10) }));

// const TOTAL_RETAILERS     = 142;
// const ACTIVE_RETAILERS    = 98;
// const TOTAL_CP            = 63;
// const WARRANTY_SOLD_TARGET = 1500;

// // ── Date Range Context ─────────────────────────────────────────────────────
// const DateRangeCtx = createContext(null);
// function useDateRange() { return useContext(DateRangeCtx); }

// // ── Filter hook (replaces useSlice) ───────────────────────────────────────
// function useRange(data) {
//   const { from, to } = useDateRange();
//   return useMemo(
//     () => data.filter(d => d.date >= from && d.date <= to),
//     [data, from, to]
//   );
// }

// // ── Date Range Picker ──────────────────────────────────────────────────────
// const PRESETS = [
//   { label: "7d",  days: 7 },
//   { label: "14d", days: 14 },
//   { label: "30d", days: 30 },
//   { label: "60d", days: 60 },
//   { label: "90d", days: 90 },
// ];

// export function DateRangePicker({ from, to, onChange }) {
//   const [activePreset, setActivePreset] = useState("30d");

//   function applyPreset(days, label) {
//     setActivePreset(label);
//     const f = toDateStr(offsetDate(TODAY, -(days - 1)));
//     const t = toDateStr(TODAY);
//     onChange(f, t);
//   }

//   function handleFrom(e) {
//     setActivePreset("custom");
//     onChange(e.target.value, to);
//   }
//   function handleTo(e) {
//     setActivePreset("custom");
//     onChange(from, e.target.value);
//   }

//   const dayCount = useMemo(() => {
//     const diff = (new Date(to) - new Date(from)) / 86400000 + 1;
//     return Math.max(0, Math.round(diff));
//   }, [from, to]);

//   return (
//     <div className="bg-white rounded-2xl border border-slate-100 px-4 py-3 flex flex-wrap items-center gap-3 mb-4">
//       {/* Icon + label */}
//       <div className="flex items-center gap-1.5 text-xs text-slate-400">
//         <i className="ti ti-calendar text-sm" />
//         <span>Date range</span>
//       </div>

//       {/* Presets */}
//       <div className="flex gap-1.5 flex-wrap">
//         {PRESETS.map(p => (
//           <button
//             key={p.label}
//             onClick={() => applyPreset(p.days, p.label)}
//             className={`text-xs px-3 py-1 rounded-full border transition-all font-medium ${
//               activePreset === p.label
//                 ? "bg-[#6C63FF] text-white border-[#6C63FF]"
//                 : "bg-white text-slate-500 border-slate-200 hover:border-[#6C63FF] hover:text-[#6C63FF]"
//             }`}
//           >
//             Last {p.label}
//           </button>
//         ))}
//       </div>

//       {/* Divider */}
//       <div className="w-px h-5 bg-slate-100" />

//       {/* Custom date inputs */}
//       <div className="flex items-center gap-2">
//         <input
//           type="date"
//           value={from}
//           max={to}
//           onChange={handleFrom}
//           className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 focus:outline-none focus:border-[#6C63FF]"
//         />
//         <span className="text-slate-300 text-xs">→</span>
//         <input
//           type="date"
//           value={to}
//           min={from}
//           max={toDateStr(TODAY)}
//           onChange={handleTo}
//           className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 focus:outline-none focus:border-[#6C63FF]"
//         />
//       </div>

//       {/* Badge */}
//       <span className="ml-auto text-[11px] font-medium px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600">
//         {dayCount} day{dayCount !== 1 ? "s" : ""} selected
//       </span>
//     </div>
//   );
// }

// // ── Shared sub-components (unchanged) ────────────────────────────────────
// function MetricCards({ items }) {
//   return (
//     <div className="flex gap-2 mb-3">
//       {items.map((m) => (
//         <div key={m.label} className="flex-1 bg-slate-50 rounded-xl px-3 py-2.5">
//           <p className="text-[10px] text-slate-400 mb-0.5">{m.label}</p>
//           <p className="text-base font-semibold" style={{ color: m.color }}>{m.value}</p>
//         </div>
//       ))}
//     </div>
//   );
// }

// function LegendRow({ items }) {
//   return (
//     <div className="flex flex-wrap gap-3 mt-2">
//       {items.map((item) => (
//         <span key={item.label} className="flex items-center gap-1.5 text-[11px] text-slate-500">
//           <span className="w-2 h-2 rounded-sm inline-block flex-shrink-0" style={{ background: item.color }} />
//           {item.label}
//         </span>
//       ))}
//     </div>
//   );
// }

// function CustomTooltip({ active, payload, label }) {
//   if (!active || !payload?.length) return null;
//   const seen = new Set();
//   const items = payload.filter(p => { if (seen.has(p.dataKey)) return false; seen.add(p.dataKey); return true; });
//   return (
//     <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-lg text-xs min-w-[120px]">
//       <p className="text-slate-400 font-medium mb-1.5 text-[10px] uppercase tracking-wide">{label}</p>
//       {items.map((p, i) => (
//         <div key={i} className="flex justify-between gap-4 mb-0.5">
//           <span className="flex items-center gap-1.5 text-slate-500">
//             <span className="w-1.5 h-1.5 rounded-sm inline-block" style={{ background: p.color || p.stroke }} />
//             {p.name}
//           </span>
//           <span className="font-semibold text-slate-800">{p.value}</span>
//         </div>
//       ))}
//     </div>
//   );
// }

// const axisProps = { axisLine: false, tickLine: false, tick: { fontSize: 9, fill: "#94a3b8" } };
// function xInterval(len) { return len > 20 ? Math.floor(len / 8) : 1; }

// // ── 1. Retailer Onboarding ─────────────────────────────────────────────────
// export function OnboardingChart() {
//   const d = useRange(onboardingData);
//   const total = d.reduce((s, r) => s + r.count, 0);
//   const peak = d.reduce((a, b) => (b.count > a.count ? b : a), d[0] || {});
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Retailer Onboarding</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-4">
//         Total: <span className="text-blue-600 font-semibold">{total}</span> · Peak:{" "}
//         <span className="text-blue-800 font-semibold">{peak.count || 0}</span>
//       </p>
//       <ResponsiveContainer width="100%" height={160}>
//         <BarChart data={d} barSize={d.length > 20 ? 6 : 10} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//           <CartesianGrid vertical={false} stroke="#f1f5f9" />
//           <XAxis dataKey="day" {...axisProps} interval={xInterval(d.length)} />
//           <YAxis {...axisProps} allowDecimals={false} />
//           <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
//           <Bar dataKey="count" name="Onboarded" radius={[3, 3, 0, 0]}>
//             {d.map((e, i) => (
//               <Cell key={i} fill={e.count === 0 ? "#dbeafe" : e.count === peak.count ? "#1d4ed8" : "#3b82f6"} />
//             ))}
//           </Bar>
//         </BarChart>
//       </ResponsiveContainer>
//       <LegendRow items={[{ label: "Onboarded", color: "#3b82f6" }, { label: "No activity", color: "#dbeafe" }, { label: "Peak day", color: "#1d4ed8" }]} />
//     </div>
//   );
// }

// // ── 2. Retailer Status Analysis ────────────────────────────────────────────
// const RETAILER_SERIES = [
//   { key: "active",   name: "Active",             color: "#22c55e" },
//   { key: "using",    name: "Using",              color: "#3b82f6" },
//   { key: "notUsing", name: "Not using",          color: "#f59e0b" },
//   { key: "pending",  name: "Pending activation", color: "#a855f7" },
//   { key: "inactive", name: "Inactive",           color: "#ef4444" },
// ];

// export function RetailerAnalysisChart() {
//   const d = useRange(retailerStatus);
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Retailer Status Analysis</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-4">Daywise breakdown by retailer status</p>
//       <ResponsiveContainer width="100%" height={160}>
//         <BarChart data={d} barSize={d.length > 20 ? 4 : 8} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//           <CartesianGrid vertical={false} stroke="#f1f5f9" />
//           <XAxis dataKey="day" {...axisProps} interval={xInterval(d.length)} />
//           <YAxis {...axisProps} allowDecimals={false} />
//           <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
//           {RETAILER_SERIES.map((s, i) => (
//             <Bar key={s.key} dataKey={s.key} name={s.name} stackId="a" fill={s.color}
//               radius={i === RETAILER_SERIES.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
//           ))}
//         </BarChart>
//       </ResponsiveContainer>
//       <LegendRow items={RETAILER_SERIES.map(s => ({ label: s.name, color: s.color }))} />
//     </div>
//   );
// }

// // ── 3. Warranty Sales ──────────────────────────────────────────────────────
// export function WarrantySalesChart() {
//   const d = useRange(warrantySales);
//   const total = d.reduce((s, r) => s + r.sales, 0);
//   const avg = Math.round(total / (d.length || 1));
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Warranty Sales</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-4">
//         Total: <span className="text-blue-600 font-semibold">{total.toLocaleString()}</span> · Daily avg:{" "}
//         <span className="text-emerald-600 font-semibold">{avg}</span>
//       </p>
//       <ResponsiveContainer width="100%" height={170}>
//         <AreaChart data={d} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//           <defs>
//             <linearGradient id="wsGrad" x1="0" y1="0" x2="0" y2="1">
//               <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
//               <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
//             </linearGradient>
//           </defs>
//           <CartesianGrid vertical={false} stroke="#f1f5f9" />
//           <XAxis dataKey="day" {...axisProps} interval={xInterval(d.length)} />
//           <YAxis {...axisProps} allowDecimals={false} />
//           <Tooltip content={<CustomTooltip />} />
//           <Area dataKey="sales" name="Warranty sales" stroke="#3b82f6" strokeWidth={2}
//             fill="url(#wsGrad)" dot={false} activeDot={{ r: 4, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }} />
//         </AreaChart>
//       </ResponsiveContainer>
//       <LegendRow items={[{ label: "Warranty sales", color: "#3b82f6" }]} />
//     </div>
//   );
// }

// // ── 4. Invoice Chart ───────────────────────────────────────────────────────
// export function InvoiceChart() {
//   const d = useRange(invoiceData);
//   const totCreated = d.reduce((s, r) => s + r.created, 0);
//   const totPaid    = d.reduce((s, r) => s + r.paid, 0);
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Invoice Created vs Paid</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-3">Daywise invoice generation and payment status</p>
//       <MetricCards items={[
//         { label: "Created", value: totCreated, color: "#3b82f6" },
//         { label: "Paid",    value: totPaid,    color: "#22c55e" },
//         { label: "Pending", value: totCreated - totPaid, color: "#f59e0b" },
//       ]} />
//       <ResponsiveContainer width="100%" height={130}>
//         <LineChart data={d} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//           <CartesianGrid vertical={false} stroke="#f1f5f9" />
//           <XAxis dataKey="day" {...axisProps} interval={xInterval(d.length)} />
//           <YAxis {...axisProps} allowDecimals={false} />
//           <Tooltip content={<CustomTooltip />} />
//           <Line dataKey="created" name="Created" stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }} />
//           <Line dataKey="paid"    name="Paid"    stroke="#22c55e" strokeWidth={2} dot={false} strokeDasharray="4 3" activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }} />
//         </LineChart>
//       </ResponsiveContainer>
//       <LegendRow items={[{ label: "Created", color: "#3b82f6" }, { label: "Paid", color: "#22c55e" }]} />
//     </div>
//   );
// }

// // ── 5. Lead Chart ─────────────────────────────────────────────────────────
// export function LeadChart() {
//   const d = useRange(leadData);
//   const totCreated = d.reduce((s, r) => s + r.created, 0);
//   const totWon     = d.reduce((s, r) => s + r.won,     0);
//   const rate = totCreated ? Math.round((totWon / totCreated) * 100) : 0;
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Lead Created vs Won</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-3">Daywise lead pipeline and conversion</p>
//       <MetricCards items={[
//         { label: "Total leads", value: totCreated, color: "#818cf8" },
//         { label: "Won",         value: totWon,     color: "#34d399" },
//         { label: "Conversion",  value: `${rate}%`, color: "#a855f7" },
//       ]} />
//       <ResponsiveContainer width="100%" height={130}>
//         <BarChart data={d} barSize={d.length > 20 ? 4 : 8} barGap={2} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//           <CartesianGrid vertical={false} stroke="#f1f5f9" />
//           <XAxis dataKey="day" {...axisProps} interval={xInterval(d.length)} />
//           <YAxis {...axisProps} allowDecimals={false} />
//           <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
//           <Bar dataKey="created" name="Created" fill="#818cf8" radius={[3, 3, 0, 0]} />
//           <Bar dataKey="won"     name="Won"     fill="#34d399" radius={[3, 3, 0, 0]} />
//         </BarChart>
//       </ResponsiveContainer>
//       <LegendRow items={[{ label: "Created", color: "#818cf8" }, { label: "Won", color: "#34d399" }]} />
//     </div>
//   );
// }

// // ── 6. Product Warranty Sales ──────────────────────────────────────────────
// const PRODUCTS = [
//   { key: "mobile", name: "Mobile", color: "#818cf8" },
//   { key: "tv",     name: "TV",     color: "#34d399" },
//   { key: "ac",     name: "AC",     color: "#fb923c" },
//   { key: "laptop", name: "Laptop", color: "#38bdf8" },
// ];

// export function ProductWarrantyChart() {
//   const d = useRange(productSales);
//   const totals = PRODUCTS.reduce((acc, p) => {
//     acc[p.key] = d.reduce((s, r) => s + (r[p.key] ?? 0), 0);
//     return acc;
//   }, {});
//   return (
//     <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
//       <h3 className="text-sm font-semibold text-slate-800">Product Warranty Sales</h3>
//       <p className="text-[11px] text-slate-400 mt-0.5 mb-3">Daywise product-wise warranty sales trend</p>
//       <MetricCards items={PRODUCTS.map(p => ({ label: p.name, value: totals[p.key], color: p.color }))} />
//       <ResponsiveContainer width="100%" height={130}>
//         <LineChart data={d} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
//           <CartesianGrid vertical={false} stroke="#f1f5f9" />
//           <XAxis dataKey="day" {...axisProps} interval={xInterval(d.length)} />
//           <YAxis {...axisProps} allowDecimals={false} />
//           <Tooltip content={<CustomTooltip />} />
//           {PRODUCTS.map((p, i) => (
//             <Line key={p.key} dataKey={p.key} name={p.name} stroke={p.color} strokeWidth={2}
//               dot={false} strokeDasharray={i > 1 ? "4 3" : undefined}
//               activeDot={{ r: 4, fill: p.color, stroke: "#fff", strokeWidth: 2 }} />
//           ))}
//         </LineChart>
//       </ResponsiveContainer>
//       <LegendRow items={PRODUCTS.map(p => ({ label: p.name, color: p.color }))} />
//     </div>
//   );
// }

// // ── Donut ──────────────────────────────────────────────────────────────────
// function Donut({ pct, color = "#6C63FF", size = 88 }) {
//   const r = size * 0.4, circ = 2 * Math.PI * r;
//   return (
//     <div className="relative" style={{ width: size, height: size }}>
//       <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
//         <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f0eeff" strokeWidth={9} />
//         <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={9}
//           strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} />
//       </svg>
//       <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold" style={{ color }}>{pct}%</div>
//     </div>
//   );
// }

// function StatCard({ icon, iconBg, iconColor, value, label, arrow }) {
//   return (
//     <div className="bg-white rounded-2xl flex items-center gap-3 px-4 py-4 border border-slate-100">
//       <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg" style={{ background: iconBg, color: iconColor }}>
//         <i className={`ti ${icon}`} />
//       </div>
//       <div className="flex-1 min-w-0">
//         <p className="text-lg font-semibold text-slate-800 leading-none">{typeof value === "number" ? value.toLocaleString() : value}</p>
//         <p className="text-[11px] text-slate-400 mt-0.5">{label}</p>
//       </div>
//       {arrow && <span className="text-slate-300 text-base">›</span>}
//     </div>
//   );
// }

// function GoalBar({ label, value, target, color = "#6C63FF" }) {
//   const pct = Math.min(100, Math.round((value / target) * 100));
//   return (
//     <div className="bg-white rounded-2xl px-4 py-3.5 border border-slate-100">
//       <div className="flex items-center justify-between mb-2">
//         <span className="text-sm font-semibold text-slate-700">{label}</span>
//         <span className="text-sm font-semibold" style={{ color }}>{pct}%</span>
//       </div>
//       <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
//         <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
//       </div>
//       <p className="text-[10px] text-slate-400 mt-1.5">{value.toLocaleString()} sold · target {target.toLocaleString()}</p>
//     </div>
//   );
// }

// // ── Summary Cards (date-range-aware) ──────────────────────────────────────
// function SummaryCards() {
//   const rangedOnboard = useRange(onboardingData);
//   const rangedWAct    = useRange(warrantyActData);
//   const rangedSubs    = useRange(subscriptionData);
//   const rangedWSold   = useRange(warrantySales);

//   const wActTotal  = rangedWAct.reduce((s, r) => s + r.count, 0);
//   const onbTotal   = rangedOnboard.reduce((s, r) => s + r.count, 0);
//   const subTotal   = rangedSubs.reduce((s, r) => s + r.count, 0);
//   const wSoldTotal = rangedWSold.reduce((s, r) => s + r.sales, 0);

//   const todayStr = toDateStr(TODAY);
//   const todayOnboard = onboardingData.find(d => d.date === todayStr)?.count ?? 0;
//   const todayWAct    = warrantyActData.find(d => d.date === todayStr)?.count ?? 0;
//   const todayWSold   = warrantySales.find(d => d.date === todayStr)?.sales ?? 0;
//   const todaySubs    = subscriptionData.find(d => d.date === todayStr)?.count ?? 0;

//   const avgOnb  = Math.round(onbTotal  / (rangedOnboard.length || 1));
//   const avgWAct = Math.round(wActTotal / (rangedWAct.length    || 1));
//   const avgWSold= Math.round(wSoldTotal/ (rangedWSold.length   || 1));

//   const seatsBookedPct = Math.round((ACTIVE_RETAILERS / TOTAL_RETAILERS) * 100);

//   const todayComps = [
//     { label: "Activations", today: todayWAct,    avg: avgWAct,  color: "#2ECC71" },
//     { label: "Onboarded",   today: todayOnboard, avg: avgOnb,   color: "#F59E0B" },
//     { label: "W. Sold",     today: todayWSold,   avg: avgWSold, color: "#6C63FF" },
//   ];

//   return (
//     <div className="mb-5 space-y-3">
//       <div className="grid grid-cols-2 gap-3">
//         <div className="flex flex-col gap-3">
//           <div className="bg-white rounded-2xl border border-slate-100 flex flex-col items-center justify-center py-5 gap-2">
//             <Donut pct={seatsBookedPct} />
//             <p className="text-xs text-slate-400">Active Retailers</p>
//           </div>
//           <GoalBar label="Warranty Sold" value={wSoldTotal} target={WARRANTY_SOLD_TARGET} />
//         </div>
//         <div className="flex flex-col gap-3">
//           <StatCard icon="ti-building-store" iconBg="#FFF0E6" iconColor="#FF8C42" value={TOTAL_RETAILERS} label="Total Retailers" arrow />
//           <StatCard icon="ti-shield-check"   iconBg="#EEF9F3" iconColor="#2ECC71" value={wActTotal}       label="Warranty Activations" arrow />
//           <StatCard icon="ti-users-group"    iconBg="#EEF3FF" iconColor="#6C63FF" value={TOTAL_CP}        label="Channel Partners" arrow />
//         </div>
//       </div>
//       <div className="grid grid-cols-3 gap-3">
//         <StatCard icon="ti-user-plus"  iconBg="#FFF5E6" iconColor="#F59E0B" value={onbTotal}  label={`Onboarded`} />
//         <StatCard icon="ti-credit-card" iconBg="#FEF0F5" iconColor="#EC4899" value={subTotal} label={`Subscriptions`} />
//         <div className="bg-white rounded-2xl px-4 py-3 border border-slate-100 flex flex-col gap-2">
//           <p className="text-[10px] text-slate-400 font-medium">Today vs avg</p>
//           {todayComps.map(t => (
//             <div key={t.label} className="flex items-center justify-between">
//               <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
//                 <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
//                 {t.label}
//               </span>
//               <span className={`text-xs font-semibold ${t.today >= t.avg ? "text-emerald-600" : "text-red-500"}`}>
//                 {t.today >= t.avg ? "↑" : "↓"} {t.today}
//               </span>
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// }

// // ── Root: provides date range context to all children ─────────────────────
// export default function AdminDashboardCharts() {
//   const defaultFrom = toDateStr(offsetDate(TODAY, -29));
//   const defaultTo   = toDateStr(TODAY);

//   const [from, setFrom] = useState(defaultFrom);
//   const [to,   setTo  ] = useState(defaultTo);

//   function handleRangeChange(f, t) { setFrom(f); setTo(t); }

//   return (
//     <DateRangeCtx.Provider value={{ from, to }}>
//       {/* Global date picker — controls ALL charts */}
//       <DateRangePicker from={from} to={to} onChange={handleRangeChange} />

//       {/* Summary cards */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
//         <SummaryCards />
//         <OnboardingChart />
//       </div>

//       {/* Chart grid */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
//         <RetailerAnalysisChart />
//         <WarrantySalesChart />
//         <LeadChart />
//         <ProductWarrantyChart />
//       </div>
//     </DateRangeCtx.Provider>
//   );
// }


// import React, { useMemo, useState } from "react";
// import OverviewView from "./OverView";

// function SuperAdminPortal() {
  
//   const [view, setView] = useState("overview");
//   // Local state so admin can approve / reject / toggle etc.
//   const [retailerList, setRetailerList] = useState();
//   const [agentList] = useState();
//   const [warrantyList] = useState();
//   const [claimList, setClaimList] = useState();
//   const [payoutList, setPayoutList] = useState();
//   const [taskList, setTaskList] = useState();
//   const [legalList] = useState();
//   const [retailerUserList, setRetailerUserList] = useState();

//   // Admin actions – users & retailers 
//   const handleToggleUserStatus = (id) => {
//     setRetailerUserList((prev) =>
//       prev.map((u) =>
//         u.id === id
//           ? {
//               ...u,
//               status: u.status === "Active" ? "Inactive" : "Active",
//             }
//           : u
//       )
//     );
//   };

//   const handleToggleRetailerStatus = (id) => {
//     setRetailerList((prev) =>
//       prev.map((r) =>
//         r.id === id
//           ? {
//               ...r,
//               status: r.status === "Active" ? "Deactivated" : "Active",
//             }
//           : r
//       )
//     );
//   };

//   // Admin actions – tasks
//   const handleAddTask = (task) => {
//     setTaskList((prev) => {
//       const nextIndex = prev.length + 101;
//       return [
//         ...prev,
//         {
//           ...task,
//           id: task.id || `TSK-${nextIndex}`,
//         },
//       ];
//     });
//   };

//   const handleUpdateTask = (id, updates) => {
//     setTaskList((prev) => prev.map((t) => (t.id === id ? { ...t, ...updates } : t)));
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 flex flex-col text-slate-900">
      
//       {/* Content */}
//       <main className="flex-1 px-2 md:px-4 py-2">
//         {view === "overview" && <OverviewView  />}
//       </main>
//     </div>
//   );
// }

// export default SuperAdminPortal;
